import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Divider,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { getParameterByName } from '../util/query_param'
import { pathPrefix, applicationName } from '../env'
import { brandColors, serif } from './brand'
import logo from './erecords-logo.svg'

type Provider = { ID: string; URL: string; Hidden: boolean; Fields?: unknown[] }

// Microsoft's four-square mark, as Microsoft's sign-in button guidelines ask.
function MicrosoftMark(): React.JSX.Element {
  return (
    <svg width='20' height='20' viewBox='0 0 21 21' aria-hidden>
      <rect x='1' y='1' width='9' height='9' fill='#f25022' />
      <rect x='11' y='1' width='9' height='9' fill='#7fba00' />
      <rect x='1' y='11' width='9' height='9' fill='#00a4ef' />
      <rect x='11' y='11' width='9' height='9' fill='#ffb900' />
    </svg>
  )
}

// ErecordsLogin shows only "Sign in with Microsoft". The password form is for
// break-glass use at ?basic=1 (also IP-restricted at the ingress); the basic
// provider itself is unchanged server-side. Replaces upstream's Login.
export default function ErecordsLogin(): React.JSX.Element {
  const theme = useTheme()
  const c = brandColors[theme.palette.mode]
  const [providers, setProviders] = useState<Provider[] | null>(null)
  const [error, setError] = useState(getParameterByName('login_error') || '')
  const wantBasic = getParameterByName('basic') === '1'

  useEffect(() => {
    fetch(pathPrefix + '/api/v2/identity/providers')
      .then((res) => res.json())
      .then(setProviders)
      .catch((err) => setError(String(err)))
  }, [])

  const oidc = providers?.find((p) => p.ID === 'oidc' && !p.Hidden)
  const basic = providers?.find((p) => p.ID === 'basic' && !p.Hidden)
  const showBasic = Boolean(basic) && (wantBasic || (providers && !oidc))

  return (
    <Box
      sx={{
        minHeight: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: c.bg,
        p: 2,
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 400,
          p: { xs: 3, sm: 4 },
          borderRadius: '18px',
          border: `1px solid ${c.line}`,
          bgcolor: c.surface,
          boxShadow:
            '0 1px 2px rgba(51,49,50,0.05), 0 8px 28px rgba(51,49,50,0.07)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <img src={logo} alt='eRecords' height={22} />
          <Typography
            className='erecords-serif'
            sx={{
              fontFamily: serif,
              fontSize: 20,
              lineHeight: 1,
              pl: 1.5,
              borderLeft: `1px solid ${c.line}`,
              color: c.ink,
            }}
          >
            On-call
          </Typography>
        </Box>
        <Typography
          component='h1'
          className='erecords-serif'
          sx={{ fontFamily: serif, fontSize: 26, color: c.ink, mb: 1 }}
        >
          Sign in to {applicationName}
        </Typography>
        <Typography sx={{ color: c.muted, mb: 3, fontSize: 15 }}>
          Alerts, who is on call, and whose phone will ring.
        </Typography>

        {showBasic && (
          <form action={basic?.URL} method='post'>
            <TextField
              name='username'
              label='Username'
              autoComplete='username'
              required
              fullWidth
              margin='dense'
            />
            <TextField
              name='password'
              label='Password'
              type='password'
              autoComplete='current-password'
              required
              fullWidth
              margin='dense'
            />
            <Button
              type='submit'
              variant='contained'
              fullWidth
              sx={{ mt: 1.5, height: 44 }}
            >
              Sign in
            </Button>
            {oidc && (
              <Divider sx={{ my: 2.5, fontSize: 13, color: c.muted }}>
                or
              </Divider>
            )}
          </form>
        )}

        {(oidc || !showBasic) && (
          <form action={oidc?.URL} method='post'>
            <Button
              type='submit'
              fullWidth
              disabled={!oidc}
              variant='outlined'
              startIcon={<MicrosoftMark />}
              sx={{
                height: 48,
                textTransform: 'none',
                fontSize: 16,
                fontWeight: 600,
                color: theme.palette.mode === 'dark' ? '#fff' : '#5e5e5e',
                bgcolor: theme.palette.mode === 'dark' ? '#2f2f2f' : '#fff',
                borderColor:
                  theme.palette.mode === 'dark' ? '#5e5e5e' : '#8c8c8c',
                borderRadius: '10px',
                '&:hover': {
                  bgcolor:
                    theme.palette.mode === 'dark' ? '#3a3a3a' : '#f3f3f3',
                  borderColor:
                    theme.palette.mode === 'dark' ? '#8c8c8c' : '#5e5e5e',
                },
              }}
            >
              Sign in with Microsoft
            </Button>
          </form>
        )}

        {error && (
          <Typography
            role='alert'
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: '10px',
              fontSize: 14,
              bgcolor: c.accentSoft,
              color: theme.palette.error.main,
            }}
          >
            {error}
          </Typography>
        )}

        <Typography sx={{ mt: 3, fontSize: 13, color: c.muted }}>
          Access is granted through the eRecords groups in Microsoft Entra. Ask
          an eRecords admin if you can’t get in.
        </Typography>
      </Card>
    </Box>
  )
}
